import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonContent,
    IonModal,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const WaterResource: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeScience/WaterResource.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeScience/WaterResource.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Water as a Resource – Class 3 Science | Uses, Sources & Conservation | Mama’s Guidance";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Explore the importance of water in Class 3 Science. Learn about sources of water, its uses, properties, and how we can save water through simple steps at Mama’s Guidance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>💧 Chapter 10: Water as a Resource</h1>

                            <div className="section">
                                <h2>🌊 Importance of Water</h2>
                                <p>Water is one of the most important natural resources. All living things need water to live.</p>
                                <ul>
                                    <li>We need water to drink, cook, bathe, and clean.</li>
                                    <li>Animals need water to drink and stay cool.</li>
                                    <li>Plants need water to grow.</li>
                                </ul>
                                <div className="fact">💡 <strong>Fact:</strong> Our body is made up of more than 60% water!</div>
                            </div>

                            <div className="section">
                                <h2>🚿 Uses of Water</h2>
                                <ul>
                                    <li>Drinking</li>
                                    <li>Cooking food</li>
                                    <li>Washing clothes and utensils</li>
                                    <li>Bathing</li>
                                    <li>Watering plants</li>
                                    <li>Cleaning houses and vehicles</li>
                                </ul>
                            </div>

                            <div className="section">
                                <h2>🌧️ Sources of Water</h2>
                                <p>Water comes from:</p>
                                <ul>
                                    <li>Rain</li>
                                    <li>Rivers</li>
                                    <li>Lakes</li>
                                    <li>Ponds</li>
                                    <li>Wells</li>
                                    <li>Underground water</li>
                                </ul>
                            </div>

                            <div className="section">
                                <h2>⚗️ Properties of Water</h2>
                                <ul>
                                    <li>Water has no color, smell, or taste.</li>
                                    <li>It can dissolve many things (we call it a universal solvent).</li>
                                    <li>It flows and takes the shape of the container.</li>
                                </ul>
                                <div className="fact">🧪 Water is a liquid and is essential for all forms of life.</div>
                            </div>

                            <div className="section">
                                <h2>🔄 States of Water</h2>
                                <ul>
                                    <li><strong>Solid:</strong> Ice</li>
                                    <li><strong>Liquid:</strong> Water</li>
                                    <li><strong>Gas:</strong> Water vapor or steam</li>
                                </ul>
                                <p>Water changes its state when we heat or cool it.</p>
                            </div>

                            <div className="section">
                                <h2>🌍 Save Water</h2>
                                <p>We must use water wisely. Here’s how we can save water:</p>
                                <ul>
                                    <li>Turn off taps when not in use.</li>
                                    <li>Use a bucket instead of a shower.</li>
                                    <li>Fix leaking pipes and taps.</li>
                                    <li>Collect rainwater and use it for plants.</li>
                                    <li>Don’t pollute water bodies.</li>
                                </ul>
                            </div>

                            <div className="section">
                                <h2>✅ Quick Recap</h2>
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Topic</th>
                                            <th>Key Points</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Importance</td>
                                            <td>All living beings need water to live</td>
                                        </tr>
                                        <tr>
                                            <td>Uses</td>
                                            <td>Drinking, cooking, cleaning, bathing, gardening</td>
                                        </tr>
                                        <tr>
                                            <td>Sources</td>
                                            <td>Rain, rivers, lakes, ponds, wells</td>
                                        </tr>
                                        <tr>
                                            <td>Properties</td>
                                            <td>No color, taste, or smell; can dissolve things</td>
                                        </tr>
                                        <tr>
                                            <td>States</td>
                                            <td>Solid (ice), Liquid (water), Gas (steam)</td>
                                        </tr>
                                        <tr>
                                            <td>Save Water</td>
                                            <td>Turn off taps, fix leaks, avoid waste</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>💧 Class 3 Science – Chapter 10: Water as a Resource</h1>

                        <div className="section">
                            <h2>🌊 Importance of Water</h2>
                            <p>Water is one of the most important natural resources. All living things need water to live.</p>
                            <ul>
                                <li>We need water to drink, cook, bathe, and clean.</li>
                                <li>Animals need water to drink and stay cool.</li>
                                <li>Plants need water to grow.</li>
                            </ul>
                            <div className="fact">💡 <strong>Fact:</strong> Our body is made up of more than 60% water!</div>
                        </div>

                        <div className="section">
                            <h2>🚿 Uses of Water</h2>
                            <ul>
                                <li>Drinking</li>
                                <li>Cooking food</li>
                                <li>Washing clothes and utensils</li>
                                <li>Bathing</li>
                                <li>Watering plants</li>
                                <li>Cleaning houses and vehicles</li>
                            </ul>
                        </div>

                        <div className="section">
                            <h2>🌧️ Sources of Water</h2>
                            <p>Water comes from:</p>
                            <ul>
                                <li>Rain</li>
                                <li>Rivers</li>
                                <li>Lakes</li>
                                <li>Ponds</li>
                                <li>Wells</li>
                                <li>Underground water</li>
                            </ul>
                        </div>

                        <div className="section">
                            <h2>⚗️ Properties of Water</h2>
                            <ul>
                                <li>Water has no color, smell, or taste.</li>
                                <li>It can dissolve many things (we call it a universal solvent).</li>
                                <li>It flows and takes the shape of the container.</li>
                            </ul>
                            <div className="fact">🧪 Water is a liquid and is essential for all forms of life.</div>
                        </div>

                        <div className="section">
                            <h2>🔄 States of Water</h2>
                            <ul>
                                <li><strong>Solid:</strong> Ice</li>
                                <li><strong>Liquid:</strong> Water</li>
                                <li><strong>Gas:</strong> Water vapor or steam</li>
                            </ul>
                            <p>Water changes its state when we heat or cool it.</p>
                        </div>

                        <div className="section">
                            <h2>🌍 Save Water</h2>
                            <p>We must use water wisely. Here’s how we can save water:</p>
                            <ul>
                                <li>Turn off taps when not in use.</li>
                                <li>Use a bucket instead of a shower.</li>
                                <li>Fix leaking pipes and taps.</li>
                                <li>Collect rainwater and use it for plants.</li>
                                <li>Don’t pollute water bodies.</li>
                            </ul>
                        </div>

                        <div className="section">
                            <h2>✅ Quick Recap</h2>
                            <table>
                                <tr>
                                    <th>Topic</th>
                                    <th>Key Points</th>
                                </tr>
                                <tr>
                                    <td>Importance</td>
                                    <td>All living beings need water to live</td>
                                </tr>
                                <tr>
                                    <td>Uses</td>
                                    <td>Drinking, cooking, cleaning, bathing, gardening</td>
                                </tr>
                                <tr>
                                    <td>Sources</td>
                                    <td>Rain, rivers, lakes, ponds, wells</td>
                                </tr>
                                <tr>
                                    <td>Properties</td>
                                    <td>No color, taste, or smell; can dissolve things</td>
                                </tr>
                                <tr>
                                    <td>States</td>
                                    <td>Solid (ice), Liquid (water), Gas (steam)</td>
                                </tr>
                                <tr>
                                    <td>Save Water</td>
                                    <td>Turn off taps, fix leaks, avoid waste</td>
                                </tr>
                            </table>
                        </div>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default WaterResource;
